import React from 'react';
import { Link } from 'react-router-dom';
import { format as formatDate } from 'date-fns';
import { Button, FieldGroup } from '@ver-uds/react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { Account, CompanyInfo } from 'eve-account-client';
import { useEmployeeRangesQuery, useNaicsSectorsQuery, useNaicsSubSectorByIdQuery } from '../services';
import Section from './Section';
import './CompanyInformation.css';

type OnButtonClick = { onEditButtonClick: () => void; editUrl?: never };
type EditUrl = { editUrl: string; onEditButtonClick?: never };
export type CompanyInformationProps = {
  isCorporateAdmin?: boolean;
  accountData?: Pick<Account, 'id' | 'mouSignedDate'>;
  companyInfoData?: Pick<
    CompanyInfo,
    'employeeRangeId' | 'naicsCode' | 'employerIdNumber' | 'ueiSAM' | 'dunsNumber' | 'companyName' | 'dba'
  >;
} & (OnButtonClick | EditUrl);

const CompanyInformation = ({
  onEditButtonClick,
  isCorporateAdmin = false,
  editUrl,
  accountData,
  companyInfoData,
}: CompanyInformationProps): JSX.Element => {
  const { isLoading: naicsSubSectorIsLoading, data: naicsSubSectorData } = useNaicsSubSectorByIdQuery(
    companyInfoData?.naicsCode,
  );
  const { isLoading: naicsSectorsIsLoading, data: naicsSectorsData } = useNaicsSectorsQuery();
  const { isLoading: employeeRangesIsLoading, data: employeeRangesData } = useEmployeeRangesQuery();
  const isLoading = employeeRangesIsLoading || naicsSectorsIsLoading || naicsSubSectorIsLoading;

  const CompanyFields = (): JSX.Element => (
    <>
      <FieldGroup.Field className="tablet:grid-col-6" name="Employer Identification Number (EIN)">
        {companyInfoData?.employerIdNumber ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="Unique Entity Identifier (UEI)">
        {companyInfoData?.ueiSAM ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="DUNS Number">
        {companyInfoData?.dunsNumber ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="Total Number of Employees">
        {employeeRangesData?.find(({ id }) => id === companyInfoData?.employeeRangeId)?.displayName ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="NAICS Code">
        {companyInfoData?.naicsCode ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="Sector">
        {naicsSectorsData?.find(({ code }) => code === naicsSubSectorData?.sectorCode)?.displayName ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="Subsector">
        {naicsSubSectorData?.displayName ?? '---'}
      </FieldGroup.Field>
    </>
  );

  const CorporateFields = (): JSX.Element => (
    <>
      <FieldGroup.Field className="tablet:grid-col-6" name="Unique Entity Identifier (UEI)">
        {companyInfoData?.ueiSAM ?? '---'}
      </FieldGroup.Field>
      <FieldGroup.Field className="tablet:grid-col-6" name="DUNS Number">
        {companyInfoData?.dunsNumber ?? '---'}
      </FieldGroup.Field>
    </>
  );

  const DisplayField = (): JSX.Element => (isCorporateAdmin ? <CorporateFields /> : <CompanyFields />);

  return (
    <Section title="Company Information" data-testid="company-information">
      {isLoading ? (
        <div className="margin-top-4 margin-bottom-3">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <FieldGroup className="grid-row grid-gap">
          <FieldGroup.Field className="tablet:grid-col-6" name="Company Name">
            {companyInfoData?.companyName ?? '---'}
          </FieldGroup.Field>
          <FieldGroup.Field className="tablet:grid-col-6" name="Doing Business As (DBA) Name">
            {companyInfoData?.dba ?? '---'}
          </FieldGroup.Field>
          {accountData ? (
            <>
              <FieldGroup.Field className="tablet:grid-col-6" name="Company ID">
                {accountData?.id}
              </FieldGroup.Field>
              <FieldGroup.Field className="tablet:grid-col-6" name="Enrollment Date">
                {accountData?.mouSignedDate ? formatDate(accountData.mouSignedDate, 'MMM dd, yyyy') : '---'}
              </FieldGroup.Field>
            </>
          ) : (
            <></>
          )}
          <DisplayField />
        </FieldGroup>
      )}
      {editUrl && (
        <Link to={editUrl} className="usa-button usa-button--outline">
          Edit Company Information
        </Link>
      )}

      {onEditButtonClick && (
        <Button variant="secondary" type="button" onClick={onEditButtonClick}>
          Edit Company Information
        </Button>
      )}
    </Section>
  );
};

export default CompanyInformation;
